import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api.js';
import type { TicketType } from '@command-floor/shared-types';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf'];
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

interface AttachmentState {
  file: File;
  uploading: boolean;
  error: string | null;
}

interface TicketCreatedResponse {
  id: string;
  type: TicketType;
  status: string;
  createdAt: string;
}

export function SubmitRequestButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="rounded-lg border border-aurora px-3 py-1.5 text-xs text-slate-400 transition-colors hover:border-aurora-strong hover:text-slate-200"
      >
        Submit Request
      </button>
      {open && createPortal(<SubmitRequestModal onClose={() => setOpen(false)} />, document.body)}
    </>
  );
}

function SubmitRequestModal({ onClose }: { onClose: () => void }) {
  const [type, setType] = useState<TicketType>('BUG');
  const [description, setDescription] = useState('');
  const [attachment, setAttachment] = useState<AttachmentState | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (attachment?.file) {
        const fd = new FormData();
        fd.append('type', type);
        fd.append('description', description.trim());
        fd.append('file', attachment.file);
        const res = await fetch('/api/support-tickets', {
          method: 'POST',
          credentials: 'include',
          body: fd,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          const msg = body?.error?.message ?? 'Submission failed';
          throw new ApiError(res.status, { error: { code: 'SUBMIT_ERROR', message: msg } });
        }
        return res.json() as Promise<TicketCreatedResponse>;
      }
      return api.post<TicketCreatedResponse>('/api/support-tickets', {
        type,
        description: description.trim(),
      });
    },
    onSuccess: () => {
      setSubmitted(true);
      setError(null);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    },
  });

  function handleFile(file: File | undefined) {
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setAttachment({ file, uploading: false, error: 'File type not allowed (PNG, JPG, GIF, WEBP, PDF only)' });
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setAttachment({ file, uploading: false, error: 'File exceeds 10 MB limit' });
      return;
    }
    setAttachment({ file, uploading: false, error: null });
  }

  const canSubmit =
    description.trim().length >= 10 &&
    attachment?.uploading !== true &&
    (attachment?.error == null) &&
    !submitMutation.isPending;

  function close() {
    if (!submitMutation.isPending) onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6">
      <div className="w-full max-w-lg rounded-lg border border-aurora bg-surface-panel p-6 shadow-2xl">
        {submitted ? (
          <div className="py-4 text-center">
            <p className="mb-1 text-base font-semibold text-slate-100">Request submitted</p>
            <p className="mb-6 text-sm text-slate-400">We'll look into it. Thanks for the feedback.</p>
            <button
              onClick={onClose}
              className="rounded-md bg-flag-blue px-5 py-2 text-sm font-medium text-white hover:bg-blue-600"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-100">Submit a request</h2>
              <button onClick={close} className="text-slate-500 hover:text-slate-300 text-lg leading-none">
                ×
              </button>
            </div>

            {/* Type selector */}
            <div className="mb-4 grid grid-cols-2 gap-3">
              {(['BUG', 'FEATURE_REQUEST'] as TicketType[]).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`rounded-md border px-4 py-3 text-left transition-colors ${
                    type === t
                      ? 'border-flag-blue bg-flag-blue/10 text-slate-100'
                      : 'border-aurora text-slate-400 hover:border-aurora-strong hover:text-slate-200'
                  }`}
                >
                  <p className="text-sm font-medium">
                    {t === 'BUG' ? '🐛 Bug Report' : '✨ Feature Request'}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {t === 'BUG'
                      ? 'Something is broken or not working as expected'
                      : "An idea or improvement you'd like to see"}
                  </p>
                </button>
              ))}
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="mb-1 block text-sm text-slate-300">
                {type === 'BUG' ? 'Describe the bug' : 'Describe the feature'}
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder={
                  type === 'BUG'
                    ? 'What happened? What did you expect to happen?'
                    : 'What would you like to be able to do?'
                }
                className="w-full resize-none rounded-md border border-aurora-strong bg-surface px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-flag-blue focus:outline-none"
              />
              {description.trim().length > 0 && description.trim().length < 10 && (
                <p className="mt-1 text-xs text-slate-500">
                  {10 - description.trim().length} more characters needed
                </p>
              )}
            </div>

            {/* File attachment */}
            <div className="mb-5">
              <label className="mb-1 block text-sm text-slate-300">
                Attachment <span className="text-slate-500">(optional)</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.gif,.webp,.pdf"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              {!attachment ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    handleFile(e.dataTransfer.files?.[0]);
                  }}
                  className={`flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed px-4 py-5 transition-colors ${
                    dragging
                      ? 'border-flag-blue bg-flag-blue/5'
                      : 'border-aurora hover:border-aurora-strong'
                  }`}
                >
                  <span className="text-xl mb-1">📎</span>
                  <p className="text-sm text-slate-400">Click or drag a file here</p>
                  <p className="text-xs text-slate-600 mt-0.5">PNG, JPG, GIF, WEBP, PDF · max 10 MB</p>
                </div>
              ) : (
                <div className={`flex items-center gap-3 rounded-md border px-3 py-2.5 ${attachment.error ? 'border-red-700 bg-red-900/20' : 'border-aurora bg-surface'}`}>
                  <span className="text-sm text-slate-300 flex-1 truncate">{attachment.file.name}</span>
                  {attachment.error ? (
                    <span className="text-xs text-red-400">{attachment.error}</span>
                  ) : (
                    <span className="text-xs text-green-400">Ready</span>
                  )}
                  <button
                    type="button"
                    onClick={() => { setAttachment(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className="text-slate-500 hover:text-slate-300 text-sm leading-none"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

            {error && (
              <p className="mb-4 rounded-md bg-red-900/30 px-3 py-2 text-sm text-red-300">{error}</p>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={close}
                disabled={submitMutation.isPending}
                className="rounded-md border border-aurora px-4 py-2 text-sm text-slate-400 hover:border-aurora-strong hover:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => submitMutation.mutate()}
                disabled={!canSubmit}
                className="rounded-md bg-flag-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitMutation.isPending ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
